/**
 * Retro stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const retroStickersPack: ToilePack = {
    slug: 'retro-stickers',
    category: 'sticker',
    name: 'Retro stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'smiley', label: 'Smiley', subject: 'a classic yellow smiley face'},
        {key: 'rainbow', label: 'Rainbow arc', subject: 'a three-band rainbow arc with a cloud at each end'},
        {key: 'groovy', label: 'Groovy badge', subject: 'a round badge lettered "GROOVY" in bubbly 70s type'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a peace sign instead', 'a whole sticker sheet', 'in orange and brown'],
    directionHint: 'try: a peace sign instead',
    seo: {
        title: 'Retro Stickers: Smiley, Rainbow Arc, Groovy Badge',
        metaDescription: 'Nine 70s-style stickers — a smiley face, a rainbow arc, a lettered groovy badge — in retro, bold cartoon, and minimal line styles.',
        h1: 'Retro stickers: smiley face, rainbow arc, groovy badge',
        tagline: 'Nine stickers, three 70s icons, three styles each.',
        intro: "A classic yellow smiley face, a three-band rainbow arc with a cloud at each end, and a round badge lettered 'GROOVY' in bubbly 70s type — three retro stickers, die-cut and ready to print. Each rendered retro, bold cartoon, and minimal single-line. Ask for a peace sign instead, a sunset scene, or a flower-power composition, and the same three styles carry it.",
        useCases: [
            'a laptop or water bottle',
            'a planner or journal page',
            'a phone case sticker sheet',
            'a retro-themed party favour',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine retro stickers — a smiley face, a rainbow arc, and a groovy badge — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a peace sign or a different 70s icon?', a: 'Yes — the direction field takes any variation, including a peace sign instead, a sunset scene, or a flower-power composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Does the badge carry a real brand name?', a: "No — the lettering is a generic word ('GROOVY'), not a real brand or logo."},
        ],
    },
}
